import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

/**
 * Link a un PDF mostrato nel tab Note. `country` = paese per l'etichetta "Note {paese}".
 */
interface NotesPdfLink {
  country?: string;
  label: string;
  url: string;
}

/**ToDo Documentazione */
@Component({
  selector: 'app-notes',
  templateUrl: './notes.component.html',
  styleUrls: ['./notes.component.scss']
})
export class NotesComponent implements OnInit, OnChanges {
  @Input() public generalNotes: any;
  @Input() public countryNotes: any;
  @Input() public supplierNotes: any;
  @Input() public insurances: any;
  /** Parametro tenant `generalPdf`: pdf generali configurati (etichetta per lingua). */
  @Input() public generalPdf: any;
  @Input() public isTravelAgency: boolean = false;
  /** Gate tenant (`enableNotesPdf`): assente/false ⇒ tab invariato. */
  @Input() public pdfLinksEnabled: boolean = false;
  /** Lingua corrente, usata per scegliere l'etichetta dei pdf generali. */
  @Input() public language: string = '';
  /** Consolidator del tenant, per risolvere i file `generalPdf` relativi. */
  @Input() public consolidator: string = '';

  public filteredGeneralNotes: any[] = [];
  public filteredCountryNotes: any[] = [];
  public filteredSupplierNotes: any[] = [];
  public filteredStatementNotes: any[] = [];
  public generalPdfLinks: NotesPdfLink[] = [];
  public countryPdfLinks: NotesPdfLink[] = [];
  public insurancePdfLinks: NotesPdfLink[] = [];
  public generalConfigPdfLinks: NotesPdfLink[] = [];
  /** Grafico "Info Generali" (gate ON): griglia di card link, una per PDF presente. */
  public pdfLinkCards: NotesPdfLink[] = [];

  constructor() { }

  ngOnInit(): void {
    this.updateFilteredNotes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.generalNotes || changes.countryNotes || changes.supplierNotes ||
      changes.insurances || changes.generalPdf || changes.pdfLinksEnabled ||
      changes.language || changes.consolidator) {
      this.updateFilteredNotes();
    }
  }

  private updateFilteredNotes(): void {
    this.filteredGeneralNotes = this.filterEmptyNotes(this.generalNotes);
    this.filteredCountryNotes = this.filterEmptyNotes(this.countryNotes);
    this.filteredSupplierNotes = this.filterEmptyNotes(this.supplierNotes);
    this.filteredStatementNotes = this.filterStatementNotes(this.generalNotes);
    this.updatePdfLinks();
  }

  /** I link ai PDF sono calcolati solo a gate attivo: altrimenti tutto invariato. */
  private updatePdfLinks(): void {
    if (!this.pdfLinksEnabled) {
      this.generalPdfLinks = [];
      this.countryPdfLinks = [];
      this.insurancePdfLinks = [];
      this.generalConfigPdfLinks = [];
      this.pdfLinkCards = [];

      return;
    }

    this.generalPdfLinks = this.mapPdfLinks(this.generalNotes, note => note.label);
    this.countryPdfLinks = this.mapPdfLinks(
      this.countryNotes,
      note => note.label || note.countryName || note.country_name
    );
    this.insurancePdfLinks = this.mapPdfLinks(this.insurances, insurance => insurance.name);
    this.generalConfigPdfLinks = this.mapGeneralPdfConfig(this.generalPdf);
    this.pdfLinkCards = this.buildPdfCards();
  }

  /**
   * Card del tab con gate attivo: generali (pratica + config) → nazioni → assicurazioni,
   * deduplicate per url (lo stesso pdf comparirebbe più volte, es. polizze per pax).
   */
  private buildPdfCards(): NotesPdfLink[] {
    const cards: NotesPdfLink[] = [];
    const seen: Set<string> = new Set<string>();

    const push = (link: NotesPdfLink) => {
      if (link && link.url && !seen.has(link.url)) {
        seen.add(link.url);
        cards.push(link);
      }
    };

    this.generalPdfLinks.forEach(push);
    this.generalConfigPdfLinks.forEach(push);
    this.mapCountryCards().forEach(push);
    this.insurancePdfLinks.forEach(push);

    return cards;
  }

  /** Card note nazione: label dalla pratica, altrimenti "Note {paese}" dal template. */
  private mapCountryCards(): NotesPdfLink[] {
    if (!this.countryNotes || !Array.isArray(this.countryNotes)) {
      return [];
    }

    return this.countryNotes
      .filter(note => !!note && this.hasDocumentUrl(note.documentUrl))
      .map(note => ({
        country: (note.countryName || note.country_name || '').trim(),
        label: (note.label || '').trim(),
        url: note.documentUrl.trim()
      }));
  }

  /** Costruisce i link dalle note/assicurazioni: solo se `documentUrl` è valorizzato. */
  private mapPdfLinks(items: any[], labelFn: (item: any) => string): NotesPdfLink[] {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    return items
      .filter(item => !!item && this.hasDocumentUrl(item.documentUrl))
      .map(item => ({
        label: (labelFn(item) || '').trim(),
        url: item.documentUrl.trim()
      }));
  }

  private mapGeneralPdfConfig(config: any): NotesPdfLink[] {
    if (!Array.isArray(config)) {
      return [];
    }

    return config
      .map((entry): NotesPdfLink | undefined => {
        if (!entry || typeof entry !== 'object') {
          return undefined;
        }
        const url = this.resolveGeneralPdfUrl(entry.documentFile);
        if (!url) {
          return undefined;
        }

        return { label: this.resolveGeneralPdfLabel(entry.labels, entry.documentFile), url };
      })
      .filter((link): link is NotesPdfLink => !!link);
  }

  /** Etichetta del pdf generale: lingua corrente, altrimenti prima disponibile. */
  private resolveGeneralPdfLabel(labels: any[], fallback: string): string {
    if (Array.isArray(labels) && labels.length > 0) {
      const lang = (this.language || '').toLowerCase();
      const match = labels.find(label => (label?.language || '').toLowerCase() === lang);
      const chosen = match || labels[0];
      const value = (chosen?.label || '').trim();
      if (value) {
        return value;
      }
    }

    return fallback || '';
  }

  /** Url del pdf generale: url/path assoluto così com'è, altrimenti sotto il tenant. */
  private resolveGeneralPdfUrl(file: string): string {
    const value = (file || '').trim();
    if (!value) {
      return '';
    }
    if (/^(https?:)?\/\//i.test(value) || value.startsWith('/') || value.startsWith('assets/')) {
      return value;
    }

    return this.consolidator ? `assets/custom/${this.consolidator}/documents/${value}` : value;
  }

  private hasDocumentUrl(documentUrl: any): boolean {
    return typeof documentUrl === 'string' && documentUrl.trim() !== '';
  }

  private filterStatementNotes(notes: any[]): any[] {
    if (!notes || !Array.isArray(notes)) {
      return [];
    }

    return this.filterEmptyNotes(notes).filter(note => note.type === 'STATEMENT');
  }

  private filterEmptyNotes(notes: any[]): any[] {
    if (!notes || !Array.isArray(notes)) {
      return [];
    }

    return notes.filter(note => {
      if (!note || !note.text) {
        return false;
      }

      // Rimuovi spazi bianchi e controlla se il testo è vuoto o contiene solo <div><br />\r\n</div>
      const cleanText = note.text.trim();
      const emptyDivPattern = /^<div><br\s*\/?\s*>\s*<\/div>$/i;

      return cleanText !== '' && !emptyDivPattern.test(cleanText);
    });
  }

}
